/**
 * useDeleteDocumentAction — the shared confirm → DELETE /documents/:id → invalidate
 * flow behind every "Löschen" control on the policy surfaces (DMS Part2 P2-13,
 * dms/spec-part2-stefan-delta.md §1). Reuses the SAME endpoint + confirm copy as
 * `DocumentsScreen.handleDelete` (dms/spec.md §M11) — no new API. The one addition
 * over that precedent: it ALSO invalidates the `policies` query branch (list/detail/
 * versions all key off that one prefix — see `qk.policy`/`qk.policies`/
 * `qk.policyVersions`), since a policy surface's cached version/detail data embeds
 * the deleted document's id/state.
 *
 * The permission (`document.delete`) + business-rule (`state === 'inactive'`) gate
 * is each caller's job (e.g. `DeleteDocumentButton`) — this hook only performs the
 * confirmed action once a caller decides to invoke it.
 *
 * [Source: apps/web/src/features/documents/DocumentsScreen.tsx#handleDelete]
 * [Source: projects/broker-ag/dms/spec-part2-stefan-delta.md §1.3]
 */
import { useQueryClient } from '@tanstack/react-query';
import { apiClient } from '../../../lib/api-client';

export function useDeleteDocumentAction() {
  const qc = useQueryClient();

  /** Confirms, deletes, invalidates. Returns whether the delete actually happened. */
  async function deleteDocument(documentId: string, label: string): Promise<boolean> {
    if (!window.confirm(`${label} endgültig löschen? Das kann nicht rückgängig gemacht werden.`)) {
      return false;
    }
    try {
      await apiClient.documents.delete(documentId);
      await Promise.all([
        qc.invalidateQueries({ queryKey: ['policies'] }),
        qc.invalidateQueries({ queryKey: ['documents'] }),
      ]);
      return true;
    } catch {
      window.alert('Löschen fehlgeschlagen.');
      return false;
    }
  }

  return { deleteDocument };
}
